import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CAlert, CCard, CSpinner } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilBook } from "@coreui/icons";
import type { ApiError } from "../../api/client";
import {
  usePortfolio,
  usePortfolioBuilder,
  usePortfolioDots,
  usePortfolioGrowth,
  usePortfolioMatch,
  type PortfolioAnswer,
  type PortfolioBuilderDto,
  type PortfolioDotDto,
  type PortfolioDotsDto,
  type PortfolioDto,
  type PortfolioMatch,
  type PortfolioMatchRequest,
} from "../../api/hooks/usePortfolioBuilder";
import { useToast } from "../../context/useToast";
import { helpArticlePath } from "../../routes/route-paths";
import { BuilderControls } from "./components/BuilderControls";
import { PortfolioDots } from "./components/PortfolioDots";
import { PortfolioPanel } from "./components/PortfolioPanel";
import { QuestionCard } from "./components/QuestionCard";
import { WhatIfGrowth } from "./components/WhatIfGrowth";
import {
  CHECK_ID,
  DEFAULT_AMOUNT_THOUSANDS,
  DEFAULT_SIZE,
  DEFAULT_START_MONTHS_BACK,
  FIGURE,
  REFINE_ID,
  SIDE_QUESTION_ID,
  addMonths,
  fittingSize,
  portfolioCount,
  sideOf,
} from "./portfolio-builder";
import { useEased } from "./useEased";

// Each API replica looks for a new revision once a minute on its own clock, so the one answering a
// re-check can still be on the old revision for up to a minute after another has moved on.
const RECHECK_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 15_000, 15_000, 15_000, 15_000];

interface RevisionMove {
  /** The revision the board was showing. */
  from: string;
  /** A 404 rather than a different revision, so any answer from the builder ends the wait. */
  gone: boolean;
  attempt: number;
}

/**
 * Two to six strategies that work well together, found by answering questions or tapping a dot.
 *
 * The answers live here and go to the API whole each time; the API scores every portfolio and says
 * what to ask next. A new revision restarts the page, since its portfolios are numbered afresh.
 */
export function PortfolioBuilder() {
  const { t } = useTranslation("portfolioBuilder");
  const { showInfo } = useToast();
  const builder = usePortfolioBuilder();
  const refetchBuilder = builder.refetch;
  const [move, setMove] = useState<RevisionMove | null>(null);
  const [restarts, setRestarts] = useState(0);
  const [lostTrack, setLostTrack] = useState(false);

  const handleRevisionMoved = useCallback(
    (from: string, gone: boolean) => setMove({ from, gone, attempt: 0 }),
    [],
  );

  // Asks again until the builder names a revision other than the one the board was on, then starts
  // the board again on it.
  useEffect(() => {
    if (!move) return;

    const timer = window.setTimeout(async () => {
      const latest = (await refetchBuilder()).data;
      const moved = latest === null || (latest !== undefined && (move.gone || latest.revisionId !== move.from));

      if (moved) {
        setMove(null);
        setRestarts((count) => count + 1);
        if (latest !== null) showInfo(t("page.revisionChanged"));
      } else if (move.attempt + 1 < RECHECK_DELAYS_MS.length) {
        setMove({ ...move, attempt: move.attempt + 1 });
      } else {
        setMove(null);
        setLostTrack(true);
      }
    }, RECHECK_DELAYS_MS[move.attempt]);

    return () => window.clearTimeout(timer);
    // showInfo is a new function after every toast, so following it would restart the wait.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [move, refetchBuilder]);

  const spinner = (
    <div className="d-flex justify-content-center py-5">
      <CSpinner color="primary" />
    </div>
  );

  return (
    <div className="portfolio-builder">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-start gap-2 mb-4">
        <div>
          <h1 className="mb-1">{t("page.title")}</h1>
          <p className="text-body-secondary mb-0">{t("page.description")}</p>
        </div>
        <div className="col-12 col-sm-auto d-grid">
          <Link to={helpArticlePath("portfolio-builder")} className="btn btn-outline-secondary">
            <CIcon icon={cilBook} className="me-2" />
            {t("page.help")}
          </Link>
        </div>
      </div>

      {move ? (
        <div className="d-flex flex-column align-items-center gap-3 py-5" role="status">
          <CSpinner color="primary" />
          <span className="text-body-secondary">{t("page.updating")}</span>
        </div>
      ) : lostTrack ? (
        <CAlert color="danger">{t("page.loadError")}</CAlert>
      ) : (
        <>
          {builder.isLoading && spinner}

          {!builder.isLoading && builder.error && <CAlert color="danger">{t("page.loadError")}</CAlert>}

          {!builder.isLoading && !builder.error && builder.data === null && (
            <CAlert color="info">{t("page.unavailable")}</CAlert>
          )}

          {/* Keyed on the revision, and on each restart, so the answers start again rather than being
              read against portfolios they were never given for. */}
          {builder.data && (
            <PortfolioBuilderBoard
              key={`${builder.data.revisionId}:${restarts}`}
              builder={builder.data}
              onRevisionMoved={handleRevisionMoved}
            />
          )}
        </>
      )}
    </div>
  );
}

interface Selection {
  dot: PortfolioDotDto;
  /** Its place on the path, when it was reached by tapping a step. */
  step: number | null;
}

/** A match and the dots it sits among, answered for the same side, size and instruments. */
interface View {
  match: PortfolioMatch;
  dots: PortfolioDotsDto;
}

interface PortfolioBuilderBoardProps {
  builder: PortfolioBuilderDto;
  /** An answer came from another revision, or said the builder has gone. */
  onRevisionMoved: (from: string, gone: boolean) => void;
}

function PortfolioBuilderBoard({ builder, onRevisionMoved }: PortfolioBuilderBoardProps) {
  const { t } = useTranslation("portfolioBuilder");
  const { showWarning } = useToast();
  const [history, setHistory] = useState<PortfolioAnswer[]>([]);
  const [chosenSize, setChosenSize] = useState(DEFAULT_SIZE);
  const [exclude, setExclude] = useState(0);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [questionsHidden, setQuestionsHidden] = useState(false);
  const [showPath, setShowPath] = useState(true);
  const [amountThousands, setAmountThousands] = useState(DEFAULT_AMOUNT_THOUSANDS);
  const [start, setStart] = useState(() => addMonths(builder.lastMonth, -DEFAULT_START_MONTHS_BACK));
  const [view, setView] = useState<View | null>(null);
  const [lastShown, setLastShown] = useState<PortfolioDto | null>(null);

  const side = sideOf(history);
  const sideAnswered = history.some((entry) => entry.id === SIDE_QUESTION_ID);
  const size = fittingSize(builder, side, chosenSize, exclude);
  const groupEmpty = portfolioCount(builder, side, size, exclude) === 0;

  const matchRequest = useMemo<PortfolioMatchRequest | null>(
    () =>
      groupEmpty
        ? null
        : { side, size, exclude, history: history.filter((entry) => entry.id !== SIDE_QUESTION_ID) },
    [groupEmpty, side, size, exclude, history],
  );

  const dots = usePortfolioDots(builder.revisionId, side, size, exclude, !groupEmpty);
  const match = usePortfolioMatch(builder.revisionId, matchRequest);

  // Refs are row numbers within one revision and side, so only answers about this board's revision
  // are ever drawn or asked about.
  const fresh = (revisionId: string | undefined) => revisionId === builder.revisionId;
  const settledMatch =
    !groupEmpty && match.data && !match.isPlaceholderData && fresh(match.data.revisionId) ? match.data : null;
  const settledDots =
    !groupEmpty && dots.data && !dots.isPlaceholderData && fresh(dots.data.revisionId) ? dots.data : null;

  // The board draws the last match and dots that answered the same request, until the next pair has,
  // so nothing is shown or asked for under another side's numbering while the new answer is on its way.
  if (settledMatch && settledDots && (view?.match !== settledMatch || view.dots !== settledDots)) {
    setView({ match: settledMatch, dots: settledDots });
  }
  const behind = !settledMatch || !settledDots;
  const failed = !groupEmpty && (match.isError || dots.isError);

  const viewSide = view?.match.side ?? side;
  const pick = view?.match.pick;
  const selected = selection && pick && selection.dot.ref !== pick.ref ? selection : null;
  const selectedPortfolio = usePortfolio(builder.revisionId, viewSide, selected?.dot.ref ?? null);
  const selectedLoaded =
    selected && selectedPortfolio.data && fresh(selectedPortfolio.data.revisionId) ? selectedPortfolio.data : null;

  // Null while a tapped portfolio is on its way, when the panel keeps showing the one before it.
  const target = selected ? selectedLoaded : (pick ?? null);
  if (target && target !== lastShown) {
    setLastShown(target);
  }
  const shown = target ?? lastShown;
  const growth = usePortfolioGrowth(builder.revisionId, viewSide, shown?.ref, start);

  // Every answer names the revision it came from. One that moved on, or a builder that has gone away,
  // means the page has to start again from what is current now.
  const answeredRevisions = [
    dots.data?.revisionId,
    match.data?.revisionId,
    selectedPortfolio.data?.revisionId,
    growth.data?.kind === "growth" ? growth.data.growth.revisionId : undefined,
  ];
  const moved = answeredRevisions.some((revisionId) => revisionId !== undefined && !fresh(revisionId));
  const gone = [dots.error, match.error, selectedPortfolio.error, growth.error].some(
    (error) => (error as ApiError | null)?.status === 404,
  );
  const stale = moved || gone;
  useEffect(() => {
    if (stale) onRevisionMoved(builder.revisionId, !moved);
  }, [stale, moved, builder.revisionId, onRevisionMoved]);

  const pickTarget = useMemo(
    () =>
      pick ? [Math.abs(pick.figures[FIGURE.realDd] ?? 0), pick.figures[FIGURE.avgYear] ?? 0] : null,
    [pick],
  );
  const pickAt = useEased(pickTarget, view?.match.group ?? null);

  const answer = (entry: PortfolioAnswer) => {
    setHistory((current) => [...current, entry]);
    setSelection(null);
  };
  const back = () => {
    setHistory((current) => current.slice(0, -1));
    setSelection(null);
  };
  const restart = () => {
    setHistory([]);
    setSelection(null);
  };
  const changeSize = (next: number) => {
    setChosenSize(next);
    setSelection(null);
  };
  const toggleGroup = (index: number) => {
    const next = exclude ^ (1 << index);
    const left = portfolioCount(builder, side, size, next);
    if (left === 0) {
      showWarning(t("controls.nothingLeft", { size }));
      return;
    }
    setExclude(next);
    setSelection(null);
  };
  // Dots and steps are numbered for the board's view, so they wait while it catches up with the answers.
  const selectDot = (dot: PortfolioDotDto) => {
    if (!behind) setSelection({ dot, step: null });
  };
  const selectStep = (index: number) => {
    const step = view?.match.path[index];
    if (step && !behind) setSelection({ dot: step, step: index });
  };

  const spinner = (
    <div className="d-flex justify-content-center py-5">
      <CSpinner color="primary" />
    </div>
  );

  if (stale) {
    return spinner;
  }

  if (!view && !groupEmpty) {
    return failed ? <CAlert color="danger">{t("page.portfoliosError")}</CAlert> : spinner;
  }

  const numbered =
    (sideAnswered ? 1 : 0) +
    history.filter((entry) => entry.id !== SIDE_QUESTION_ID && entry.id !== CHECK_ID && entry.id !== REFINE_ID)
      .length;
  const path = showPath && view && view.match.path.length > 1 ? view.match.path : null;
  const otherCount = view ? view.dots.dots.filter((dot) => dot.ref !== view.match.pick.ref).length : 0;

  const step = selected?.step != null ? view?.match.path[selected.step] : undefined;
  const eyebrow = !selected
    ? t("panel.yourMatch")
    : step
      ? selected.step === 0
        ? t("panel.startingPoint")
        : t("panel.step", { number: selected.step, answer: step.label })
      : t("panel.selected");

  return (
    <div className="portfolio-board">
      <CCard className="portfolio-explore">
        <QuestionCard
          hidden={questionsHidden}
          onHiddenChange={setQuestionsHidden}
          sideAnswered={sideAnswered}
          next={view?.match.next ?? null}
          failed={failed}
          numbered={numbered}
          total={1 + builder.questionCount}
          hasAnswers={history.length > 0}
          busy={behind && !failed}
          onAnswer={answer}
          onBack={back}
          onRestart={restart}
        />
        <BuilderControls
          size={size}
          onSizeChange={changeSize}
          sizeAvailable={(option) => portfolioCount(builder, side, option, exclude) > 0}
          groups={builder.instrumentGroups}
          exclude={exclude}
          onToggleGroup={toggleGroup}
          showPath={showPath}
          onShowPathChange={setShowPath}
          pathDrawn={path !== null}
          selectedDrawn={selected !== null}
          otherCount={otherCount}
        />
        {groupEmpty || !view || view.dots.bounds === null || !pickAt ? (
          <div className="portfolio-dots is-empty">{t("dots.none")}</div>
        ) : (
          <PortfolioDots
            dots={view.dots.dots}
            bounds={view.dots.bounds}
            pickRef={view.match.pick.ref}
            pickAt={pickAt}
            path={path}
            selected={selected?.dot ?? null}
            onSelect={selectDot}
            onSelectMatch={() => setSelection(null)}
            onSelectStep={selectStep}
          />
        )}
      </CCard>

      {/* Hidden when no portfolio at all fits this side and these instruments: the last match is then
          for the other side, so there is nothing true to show. */}
      {!groupEmpty && view && shown && (
        <PortfolioPanel
          builder={builder}
          side={viewSide}
          portfolio={shown}
          eyebrow={eyebrow}
          isMatch={!selected}
          onBackToMatch={() => setSelection(null)}
          stale={behind || target === null}
          loading={(behind && !failed) || (target === null && !selectedPortfolio.isError)}
          whatIf={
            <WhatIfGrowth
              firstMonth={builder.firstMonth}
              lastMonth={builder.lastMonth}
              start={start}
              onStartChange={setStart}
              amountThousands={amountThousands}
              onAmountChange={setAmountThousands}
              answer={growth.data ?? null}
              failed={growth.isError}
            />
          }
        />
      )}
    </div>
  );
}
